import React from "react";
import useTranslate from "../../hooks/useTranslate";

// Channel picker surfaces — extracted 1:1 from LoungeComposer (QAL-T6) so
// the inline post editor (frame 1m·2) reuses the drawn dropdown/sheet
// instead of growing a second copy. Markup and tokens are byte-for-byte the
// T3 composer ones (frames 1j desktop menu, 1k mobile sheet); only the
// state wiring moved to props.

export const CheckMark = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3.5 8.5l3 3 6-6.5" stroke="#2283FF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Desktop dropdown panel (1j·2) — absolutely positioned under its trigger;
// the caller owns the `relative` wrapper and the outside-click close.
export const ChannelMenuDesktop = ({ channels, value, onSelect }) => (
  <div className="hidden sm:block absolute left-0 top-[calc(100%+6px)] z-30 w-[300px] bg-white border border-[#E4E9F0] rounded-xl shadow-[0_12px_32px_rgba(17,24,39,0.10)] p-[5px]">
    {channels.map((channel) => {
      const isSel = channel.slug === value;
      return (
        <button
          key={channel.slug}
          type="button"
          onClick={() => onSelect(channel.slug)}
          className={`w-full text-left py-[7px] px-3 rounded-lg hover:bg-[#F5F7FA] ${isSel ? "bg-[#F5F7FA]" : ""}`}
        >
          <span className="flex items-center justify-between gap-2">
            <span className={`block text-[13px] text-[#111827] ${isSel ? "font-semibold" : ""}`}>
              {channel.name}
            </span>
            {isSel && <CheckMark size={13} />}
          </span>
          <span className="block mt-0.5 text-[12px] text-[#6B7280]">{channel.description}</span>
        </button>
      );
    })}
  </div>
);

// Mobile bottom sheet (1k·1) — grabber, title, channel rows with the blue
// dot, full-width Cancel. The common/BottomSheet component draws an X-close
// header and other grabber tokens, so the drawn 1k sheet stays this inline
// variant (T3 precedent).
export const ChannelSheetMobile = ({ channels, value, onSelect, onClose }) => {
  const { t } = useTranslate();
  return (
    <div className="sm:hidden fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-[rgba(17,24,39,0.40)]"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="absolute left-0 right-0 bottom-0 bg-white rounded-t-2xl shadow-[0_-12px_40px_rgba(17,24,39,0.18)] pt-2.5 px-4 pb-6 max-h-[80vh] overflow-y-auto">
        <div className="w-10 h-1 rounded-full bg-[#DDE3EB] mx-auto" />
        <p className="m-0 mt-3.5 mb-1 text-[15px] font-bold text-[#111827]">
          {t("Choose a channel")}
        </p>
        <div className="flex flex-col mt-1.5">
          {channels.map((channel, i) => {
            const isSel = channel.slug === value;
            return (
              <button
                key={channel.slug}
                type="button"
                onClick={() => onSelect(channel.slug)}
                className={`flex items-start gap-2.5 py-2.5 px-1 text-left min-h-[44px] ${
                  i < channels.length - 1 ? "border-b border-[#EEF1F5]" : ""
                }`}
              >
                <span className="w-[7px] h-[7px] rounded-full bg-[#2283FF] shrink-0 mt-1.5" />
                <span className="flex-1 min-w-0">
                  <span className={`block text-[14px] text-[#111827] ${isSel ? "font-semibold" : ""}`}>
                    {channel.name}
                  </span>
                  <span className="block mt-0.5 text-[12.5px] text-[#6B7280]">
                    {channel.description}
                  </span>
                </span>
                {isSel && (
                  <span className="mt-[3px]">
                    <CheckMark />
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-full mt-2.5 bg-white border border-[#DDE3EB] rounded-[10px] h-11 text-[13.5px] font-semibold text-[#6B7280]"
        >
          {t("Cancel")}
        </button>
      </div>
    </div>
  );
};
